

import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Package,
  ChevronRight,
  ShoppingBag,
  Calendar,
  CreditCard,
  Truck,
  CheckCircle2,
  Clock,
  XCircle,
  AlertCircle,
  Info,
  RotateCcw,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
  import { useUserOrders } from "@/hooks/useOrders";
import { formatPrice, getStatusStyle } from "@bookstore/shared";
import {getStatusIcon} from '@/utils/getStatusIcon';

const OrdersPage = () => {
  const { user } = useAuth();
  let { data: orders, isLoading } = useUserOrders(user?.uid);

  return (
    <>
      <Helmet>
        <title>My Orders | Sunrise Bookstore</title>
      </Helmet>

      <div className="min-h-screen bg-background-muted py-12">
        <div className="container-custom">
          {/* Header */}
          <div className="flex items-center justify-between mb-12">
            <h1 className="text-3xl font-extrabold tracking-tight text-text">
              My Orders
            </h1>
            <Link
              to="/products"
              className="
                inline-flex items-center gap-2 px-5 py-2.5 rounded-xl 
                bg-primary-600 text-white font-semibold
                hover:bg-primary-700 transition-all hover:-translate-y-0.5 shadow-sm
              "
            >
              Shop More
              <ChevronRight className="w-5 h-5" />
            </Link>
          </div>

          {/* Loading State */}
          {isLoading ? (
            <div className="text-center py-24 text-text-secondary animate-pulse">
              Loading your orders...
            </div>
          ) : orders?.length === 0 ? (
            /* Empty State */
            <div className="bg-background-soft rounded-2xl shadow-md border border-border py-20 text-center">
              <ShoppingBag className="w-20 h-20 text-text-secondary/30 mx-auto mb-6" />
              <h2 className="text-2xl font-bold text-text mb-2">
                No orders yet
              </h2>
              <p className="text-text-secondary mb-8">
                Start shopping to see your orders here.
              </p>
              <Link
                to="/products"
                className="
                  px-6 py-3 inline-flex items-center gap-2 
                  bg-primary-600 text-white rounded-xl font-medium 
                  hover:bg-primary-700 transition hover:-translate-y-0.5 shadow-sm
                "
              >
                Browse Books
                <ChevronRight className="w-5 h-5" />
              </Link>
            </div>
          ) : (
            /* Orders List */
            <div className="space-y-8">
              {orders?.map((order: any) => {
                const status = getStatusStyle(order.orderStatus);


                return (
                  <div
                    key={order.id}
                    className="
                      bg-background-soft border border-border 
                      rounded-2xl p-7 shadow-sm 
                      hover:shadow-lg transition-all
                    "
                  >
                    <div className="flex flex-wrap items-center justify-between gap-6 mb-5">
                      {/* Order Number */}
                      <div>
                        <p className="text-xs uppercase text-text-secondary mb-1 tracking-wider">
                          Order
                        </p>
                        <p className="text-sm font-semibold text-text">
                          #{order.id}
                        </p>
                      </div>

                      {/* Date */}
                      <div className="flex items-center gap-2 text-text-secondary">
                        <Calendar className="w-4 h-4 text-text-secondary" />
                        <p className="text-sm">
                          {order.createdAt?.toDate()
                            .toLocaleDateString("en-IN", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            })}
                        </p>
                      </div>

                      {/* Payment */}
                      <div className="flex items-center gap-2 text-text-secondary capitalize">
                        <CreditCard className="w-4 h-4 text-text-secondary" />
                        {order.paymentMethod}
                      </div>

                      {/* Total */}
                      <div className="text-right">
                        <p className="text-sm text-text-secondary mb-1">Total</p>
                        <p className="text-xl font-bold text-text">
                          {formatPrice(order.totalAmount)}
                        </p>
                      </div>

                      {/* Status */}
                                    <div
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium ${status.bg} ${status.color}`}
                          >
                            {getStatusIcon(status.icon)}
                            {status.label}
                          </div>
                    </div>

                    {/* Footer Row */}
                    <div className="flex items-center justify-between border-t border-border pt-4">
                      <p className="text-sm text-text-secondary">
                        {order.items.length} item
                        {order.items.length > 1 ? "s" : ""}
                      </p>

                      <Link
                        to={`/orders/${order.id}`}
                        className="
                          text-primary-700 font-medium flex items-center gap-1 
                          hover:gap-2 transition-all
                        "
                      >
                        View Details
                        <ChevronRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default OrdersPage;
